import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import icLogo from "./../assets/svg/ic_logo.png";
import AxiosHelper from '../api/AxiosHelper';
import Utils from '../utils';

const SUPPORT_EMAIL = 'info@alemtilsimat.com';
const RELOAD_DELAY = 1200;

const LicenseErrorPage = ({result}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef();

  // 'idle' | 'uploading' | 'done' | the text of a failure
  const [state, setState] = useState('idle');

  // An expired licence is a lapsed subscription rather than a broken install,
  // so it reads as a warning; anything else is an error.
  const expired = result?.code === 'LICENSE_EXPIRED' || !!result?.expiresAt;
  const tone = expired ? 'orange' : 'red';

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;

    // The server only stores .key files; checking here gives a readable reason
    // instead of multer's own error.
    if (!file.name.toLowerCase().endsWith('.key')) {
      setState(t('ONLY_KEY_FILE_ALLOWED'));
      return;
    }

    const formData = new FormData();
    formData.append('license', file);

    setState('uploading');
    AxiosHelper.uploadLicense(formData).then((res) => {
      if (res.status == 200) {
        setState('done');
        setTimeout(() => { window.location.reload(); }, RELOAD_DELAY);
      } else {
        setState(t('SOMETHING_WENT_WRONG'));
      }
    }).catch((err) => {
      // Previously unhandled, so a rejected upload left the screen unchanged.
      if (!err?.response) setState(t('NO_SERVER_RESPONSE'));
      else setState(err.response.data?.msg || t('SOMETHING_WENT_WRONG'));
    });
  };

  const busy = state === 'uploading';
  const done = state === 'done';
  const failure = state !== 'idle' && !busy && !done ? state : null;

  return (
    <div className="le-page">
      <div className="le-card">
        <div className="le-brand">
          <img src={icLogo} alt="logo" />
          <span>Alem Kassa</span>
        </div>

        <div className="le-body">
          <span className={`le-icon lc-tone-${tone}`}>
            {expired
              ? <ScheduleIcon style={{ fontSize: 30 }} />
              : <ReportProblemIcon style={{ fontSize: 30 }} />}
          </span>

          <span className="le-title">{result?.message?.title}</span>
          <span className="le-desc">
            {result?.message?.description}
            <br />
            {t('PLEASE_UPLOAD_VALID_LICENSE')}
          </span>

          {result?.expiresAt &&
            <span className={`le-meta lc-tone-${tone}`}>
              <span className="le-meta-label">{t('EXPIRE_DATE')}</span>
              {Utils.getTimestampDate(result?.expiresAt)}
            </span>
          }

          <div className="le-actions">
            <button type="button" className="le-btn" onClick={handleUploadClick} disabled={busy || done}>
              {busy
                ? <><CircularProgress size={15} color="inherit" />{t('UPLOADING')}</>
                : <><UploadFileIcon style={{ fontSize: 17 }} />{t('UPLOAD_LICENSE_FILE')}</>}
            </button>

            <span className={`le-feedback le-feedback-${done ? 'green' : failure ? 'red' : 'gray'}`}>
              {done && <><CheckCircleIcon style={{ fontSize: 15 }} />{t('SUCCESS')}</>}
              {failure && <><ErrorIcon style={{ fontSize: 15 }} />{failure}</>}
            </span>
          </div>

          <input type="file" accept=".key" hidden ref={fileInputRef} onChange={handleFileChange} />
        </div>

        <div className="le-foot">
          <div className="le-foot-label">{t('NEED_HELP_CONTACT_SUPPORT')}</div>
          <a className="le-mail" href={`mailto:${SUPPORT_EMAIL}`}>
            <EmailOutlinedIcon style={{ fontSize: 16 }} />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </div>
  );
};

export default LicenseErrorPage;
